import { Vector3 } from "three";
import { isNull } from "../../infra3d";
import { GEOMETRYTYPE } from "../featuretree/internal";
import { USERINPUTTYPE } from "../userinput/internal";
import { Tool } from "./internal";
import { skip } from "rxjs/operators";
/**
 * Profile Tool
 * @class ProfileTool
 * @author Benjamin Loesch, Robert Wüest
 * @extends {Tool}
 */
export class ProfileTool extends Tool {
    constructor(scene, toolhandler, options, name, group) {
        super(scene, toolhandler);
        this.tempcoordinates = [];
        this.coordinates = [];
        this.toolstate = -1;
        this.firstPoint = null;
        this.secondPoint = null;
        this.isloading = false;
        this.leftclickeventnr = null;
        this.mousemoveeventnr = null;
        this.mouseouteventnr = null;
        this.resetinputnr = null;
        this.aeventnr = null;
        this.deventnr = null;
        this.snap_mx = null;
        this.snap_my = null;
        this.snappedpos = null;
        this.debugexport = false;
        this._keyChangeSubscription = null;
        super.setOptions(options);
        this.name = name;
        this.group = group;
        //options - set the default values here....
        const defaultoptions = {
            layerstyle: this.toolhandler.defaultstyles.profiledots,
            highlightlayerstyle: null,
            basestyle: this.toolhandler.defaultstyles.orthobase,
            finishedcbf: null,
            enablecbf: null,
            disablecbf: null,
            finishinput: USERINPUTTYPE.RIGHTCLICK,
            resetinput: USERINPUTTYPE.KEY_ESC,
            resetcbf: null,
            errorcbf: null,
            profilefinishedcbf: null,
            activecursor: "crosshair",
            modifycursor: true,
            maxdist: 20,
            continousdraw: false,
            continousdrawcbf: null,
            pointsetcbf: null,
            snaplayer: this.toolhandler.snaplayers,
        };
        for (const property in defaultoptions) {
            this.options[property] = defaultoptions[property];
        }
        super.parseOptions(options);
        //create the drawing layers
        this.featurecollection = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.layerstyle, null, "mmProfileTool_internal");
        this.pointfeature = this.featurecollection.createFeature();
        this.pointfeature.geometry = this.pointfeature.createGeometry(GEOMETRYTYPE.POINT);
        this.multipointfeature = this.featurecollection.createFeature();
        this.multipointfeature.geometry = this.multipointfeature.createGeometry(GEOMETRYTYPE.MULTIPOINT);
        this.linestringfeature = this.featurecollection.createFeature();
        this.linestringfeature.geometry = this.linestringfeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        this._npoints = 0;
        this._orthoLine = null;
        this.type = "ProfileTool";
    }
    /**
     * internal function - to activate tool call mmSelectFeatureTool.enable
     * @method _enableTool
     * @protected
     */
    _enableTool() {
        this._subscribeEvents();
        this.fire(ProfileTool.enabled, this.options["cbobject"]);
        this.toolstate = 0;
        this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
    }
    /**
     * internal function - to disable tool call mmSelectFeatureTool.disable
     * @method _disableTool
     * @protected
     */
    _disableTool() {
        if (!this.enabled)
            return; //if tool is not active there is nothing to disable...
        this.reset();
        this.toolstate = -1;
        this.fire(ProfileTool.disabled, this.options["cbobject"]);
        this._unSubsribeEvents();
        this.scene.getCursorStyleHandler().setCursorStyle("default");
    }
    _subscribeEvents() {
        this.leftclickeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEUP, this._onMouseUp, this);
        this.aeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.KEY_A, this._onA, this);
        this.deventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.KEY_D, this._onD, this);
        if (this.options.resetinput != null) {
            this.resetinputnr = this.scene.userInputHandler.subscribeEvent(this.options.resetinput, this.reset, this);
        }
        if (this.options.continousdraw) {
            this.mousemoveeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEMOVE, this._onMouseMove, this);
        }
        this.mouseouteventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEOUT, this._onMouseOut, this);
        if (this.options.enablecbf != null) {
            this.on(ProfileTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.on(ProfileTool.disabled, this.options.disablecbf);
        }
        if (this.options.continousdrawcbf != null) {
            this.on(ProfileTool.continousdraw, this.options.continousdrawcbf);
        }
        if (this.options.pointsetcbf != null) {
            this.on(ProfileTool.pointset, this.options.pointsetcbf);
        }
        if (this.options.resetcbf != null) {
            this.on(ProfileTool.reset, this.options.resetcbf);
        }
        if (this.options.profilefinishedcbf != null) {
            this.on(ProfileTool.finished, this.options.profilefinishedcbf);
        }
        if (this.options.errorcbf != null) {
            this.on(ProfileTool.error, this.options.errorcbf);
        }
    }
    _unSubsribeEvents() {
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEUP, this.leftclickeventnr);
        this.leftclickeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.KEY_A, this.aeventnr);
        this.aeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.KEY_D, this.deventnr);
        this.deventnr = null;
        if (this.options.resetinput != null) {
            this.scene.userInputHandler.unSubscribeEvent(this.options.resetinput, this.resetinputnr);
            this.resetinputnr = null;
        }
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEMOVE, this.mousemoveeventnr);
        this.mousemoveeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEOUT, this.mouseouteventnr);
        this.mouseouteventnr = null;
        if (this.options.enablecbf != null) {
            this.off(ProfileTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.off(ProfileTool.disabled, this.options.disablecbf);
        }
        if (this.options.continousdrawcbf != null) {
            this.off(ProfileTool.continousdraw, this.options.continousdrawcbf);
        }
        if (this.options.pointsetcbf != null) {
            this.off(ProfileTool.pointset, this.options.pointsetcbf);
        }
        if (this.options.resetcbf != null) {
            this.off(ProfileTool.reset, this.options.resetcbf);
        }
        if (this.options.profilefinishedcbf != null) {
            this.off(ProfileTool.finished, this.options.profilefinishedcbf);
        }
        if (this.options.errorcbf != null) {
            this.off(ProfileTool.error, this.options.errorcbf);
        }
    }
    /**
     * offset for featureline segment if start is orthogonally +1
     * and redraw
     * @method _onOne
     * @private
     */
    _onA(evt) {
        this.featureoffset++;
        this._onMouseMove(this._evt);
    }
    /**
     * offset for featureline segment if start is orthogonally -1
     * and redraw
     * @method _onOne
     * @private
     */
    _onD(evt) {
        this.featureoffset--;
        this._onMouseMove(this._evt);
    }
    /**
     * continious draw after first point is set
     * @method _onMouseMove
     * @param evt
     * @private
     */
    _onMouseMove(evt) {
        if (this.pointfeature.ishidden) {
            this.pointfeature.show();
        }
        const mx = evt.offsetX;
        const my = evt.offsetY;
        this._evt = evt;
        if (this._orthoLine == null) {
            this._pos = this._checkSnaping(evt);
        }
        else {
            this._calculateOrthoLine();
            this._pos = this._extendOrthoLine(evt);
        }
        if (!this._pos || this._pos.warning) {
            //if we try to meassure in pano _pos is null
            this.fire(ProfileTool.error, this.options["cbobject"], this._pos ? this._pos.warntext : "Not implemented in PanoStream", evt);
            this.scene.getCursorStyleHandler().setCursorStyle("not-allowed");
            return;
        }
        this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
        //Update the feature for drawing
        this.pointfeature.geometry.coordinates = [[this._pos.Easting, this._pos.Northing, this._pos.Height]];
        if (this.firstPoint != null && this.secondPoint == null) {
            this.linestringfeature.geometry.coordinates[0] = [
                this.firstPoint.Easting,
                this.firstPoint.Northing,
                this.firstPoint.Height,
            ];
            this.linestringfeature.geometry.coordinates[1] = [this._pos.Easting, this._pos.Northing, this._pos.Height];
        }
        this.featurecollection.update();
        this.fire(ProfileTool.continousdraw, this.options["cbobject"], { mx: mx, my: my, pos: this._pos }, evt);
    }
    /**
     * internal function mouseout
     * @method _onMouseOut
     * @param evt
     * @private
     */
    _onMouseOut(evt) {
        this.pointfeature.hide();
        this.pointfeature.update(true);
    }
    /**
     * if snaped layers are activ position will be snaped to their features basepoints
     * otherwise position stays the same
     * @param evt
     * @returns {any}
     * @private
     */
    _checkSnaping(evt) {
        const mx = evt.offsetX;
        const my = evt.offsetY;
        if (this.options.snaplayer && !evt.altKey) {
            const snapdataarray = this.scene.getFeatureHandler().snap(mx, my, this.options.snaplayer);
            //prevent self pickcing and filter non-pointy geometries
            for (let i = snapdataarray.length - 1; i >= 0; i--) {
                if (snapdataarray[i].geometry.type != GEOMETRYTYPE.MULTIPOINT &&
                    snapdataarray[i].geometry.type != GEOMETRYTYPE.POINT) {
                    snapdataarray.splice(i, 1);
                }
            }
            if (snapdataarray.length > 0) {
                const snapdata = snapdataarray[0];
                this.snap_mx = snapdata.found_mx;
                this.snap_my = snapdata.found_my;
                this._pos.Easting = snapdata.found_easting;
                this._pos.Northing = snapdata.found_northing;
                this._pos.Height = snapdata.found_height;
                this._pos.warning = false;
                this.snappedpos = this._pos;
            }
            else {
                this.snap_mx = null;
                this.snap_my = null;
                this.snappedpos = null;
                return this.scene
                    .getMeasureHandler()
                    .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), mx, my, this.options.maxdist);
            }
            return this._pos;
        }
        else {
            return this.scene
                .getMeasureHandler()
                .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), mx, my, this.options.maxdist);
        }
    }
    /**
     * Calculates the direction vector between the start point and the closest point on the feature
     * @private
     */
    _calculateOrthoLine() {
        super.findOrthogonal(this.firstPoint);
        this._orthoLine = new Vector3();
        this._orthoLine.subVectors(this.selectedStart.getVec3(), this.firstPoint.getVec3());
        this._orthoLine.normalize();
        this._withOrthoBase = true;
    }
    /**
     * Calculates the right scalar for the direction vector applies it and calculates two possible endpoints
     * @param evt
     * @returns {MeasurePoint} endpoint that is closest to the current mouseposition
     * @private
     */
    _extendOrthoLine(evt) {
        const startPos = this.firstPoint.getVec3();
        const mousePos = this.scene
            .getMeasureHandler()
            .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), evt.offsetX, evt.offsetY, this.options.maxdist)
            .getVec3();
        const camPos = this.scene.infrascene.sceneCamera.getPosition();
        const orthoPos = new Vector3().copy(this._orthoLine);
        if (Math.abs(mousePos.x) != Infinity && Math.abs(mousePos.y) != Infinity) {
            const relativeMousePos = new Vector3().subVectors(mousePos, camPos);
            const relativeStartPos = new Vector3().subVectors(startPos, camPos);
            this._pos = this.firstPoint.copy();
            /**
             * We end up with the formula
             * startPos + distance * orthoPos = camPos + scalar * relativeMousePos
             * With distance * orthoPos as the position we are looking for
             * We dont need to know scalar to solve the term
             */
            const distance = (relativeMousePos.x * relativeStartPos.y - relativeMousePos.y * relativeStartPos.x) /
                (relativeMousePos.y * orthoPos.x - relativeMousePos.x * orthoPos.y);
            if (relativeMousePos.x * relativeStartPos.y - relativeMousePos.y * relativeStartPos.x < 0 &&
                relativeMousePos.y * orthoPos.x - relativeMousePos.x * orthoPos.y > 0) {
                this._pos.warning = true;
                this._pos.warntext = "distance too long";
            }
            else {
                orthoPos.multiplyScalar(distance);
                orthoPos.add(this.firstPoint.getVec3());
                this._pos.Easting = orthoPos.x;
                this._pos.Northing = orthoPos.y;
            }
            return this._pos;
        }
        else {
            return this.scene
                .getMeasureHandler()
                .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), evt.offsetX, evt.offsetY, this.options.maxdist);
        }
    }
    _onMouseUp(evt) {
        //both points are set; reset on right mouseclick if no secondPoint has been picked
        if (!evt.isDragging && evt.button == 0) {
            this.coordinates = [];
            this.profilepoints = [];
            if (this.firstPoint != null && this.secondPoint != null) {
                this._cleanUp();
            }
            if (this.firstPoint == null) {
                this.firstPoint = this._checkSnaping(evt);
                if (this.firstPoint.warning) {
                    this.fire(ProfileTool.error, this.options["cbobject"], this.firstPoint.warntext);
                    this.firstPoint = null;
                    return;
                }
                this.fire(ProfileTool.pointset, this.options["cbobject"], this.firstPoint);
                if (!isNull(this.selectedFeature))
                    this._calculateOrthoLine();
                // if tool click mid usage, add subscrition for image change
                if (this.toolstate === 0) {
                    this._keyChangeSubscription = this.scene.navigator.stateService.currentKey$
                        .pipe(skip(1))
                        .subscribe(() => {
                        this.fire(ProfileTool.error, this.options["cbobject"], "tool_reset_key_changed");
                        this.reset();
                    });
                }
                this.toolstate += 1;
                return;
            }
            if (this._orthoLine == null) {
                this.secondPoint = this._checkSnaping(evt);
            }
            else {
                this._calculateOrthoLine();
                this.secondPoint = this._extendOrthoLine(evt);
            }
            if (this.secondPoint.warning) {
                this.fire(ProfileTool.error, this.options["cbobject"], this.secondPoint.warntext);
                this.secondPoint = null;
                return;
            }
            // if tool click finished usage, remove subscription for image change
            if (this.toolstate === 1) {
                this._keyChangeSubscription.unsubscribe();
            }
            this.fire(ProfileTool.pointset, this.options["cbobject"], this.secondPoint);
            this.scene.getCursorStyleHandler().setCursorStyle("wait");
            this.debugexport = evt.altKey;
            let startPointVec = this.firstPoint.getVec3();
            this.coordinates.push(new Vector3(startPointVec.x, startPointVec.y, startPointVec.z));
            this.profilepoints.push([0, startPointVec.x, startPointVec.y, startPointVec.z]);
            let dirVec = this.secondPoint.getVec3();
            dirVec = dirVec.sub(this.firstPoint.getVec3());
            const totalLength = dirVec.length();
            dirVec = dirVec.normalize();
            let cumulativeLength = 0;
            let it = 0;
            const minStepSize = 0.01;
            const mi = this.scene.getCurrentGeoframe().getMeasureInfo();
            const firstDepth = (mi.depthmapmeta.focallength * mi.depthmapmeta.basis) /
                this.firstPoint.disparityInmm;
            const secondDepth = (mi.depthmapmeta.focallength * mi.depthmapmeta.basis) /
                this.secondPoint.disparityInmm;
            const firstGSD = Math.max(((mi.depthmapmeta.pixsize * 0.001) / mi.depthmapmeta.focallength) * firstDepth, minStepSize);
            const secondGSD = Math.max(((mi.depthmapmeta.pixsize * 0.001) / mi.depthmapmeta.focallength) * secondDepth, minStepSize);
            const pointstocalc = [];
            const maxpoints = Math.max(mi.depthmapmeta.width, mi.depthmapmeta.height) * 2;
            while (cumulativeLength < totalLength) {
                if (it > maxpoints) {
                    // to prevent browser crash
                    this.failed();
                    return;
                }
                const step = firstGSD + (cumulativeLength / totalLength) * (secondGSD - firstGSD);
                const vecToAdd = new Vector3().copy(dirVec).multiplyScalar(step);
                startPointVec = startPointVec.add(vecToAdd);
                pointstocalc.push({
                    order: it + 1,
                    pos: new Vector3().copy(startPointVec),
                });
                cumulativeLength += step;
                it += 1;
            }
            this._npoints = pointstocalc.length;
            // console.log(pointstocalc);
            for (const pointtocalc of pointstocalc) {
                try {
                    this.scene.getMeasureHandler().getElevationFromPosition3(pointtocalc.pos, pointtocalc.order, (X, Y, Z, id) => {
                        this.lowerPointFound(X, Y, Z, id);
                    }, this.failed, this, 0);
                }
                catch (err) {
                    let txt = "Error with GetElevationFromPosition.\n";
                    txt += "Error description: " + err.message + "\n";
                    console.log(txt);
                }
            }
        }
        else if (evt.button == 2 && !this.secondPoint) {
            this.reset();
            return;
        }
    }
    /**
     * callback function will be fired when no position was found.
     * @method failed
     */
    failed() {
        this.fire(ProfileTool.error, this.options["cbobject"]);
        this._npoints -= 1;
        this._checkAndFinish();
    }
    /**
     * callback function will be fired as soon as the lower position was found.
     * @method lowerPointFound
     * @param {MeasurePoint} lowerPos
     */
    lowerPointFound(X, Y, Z, order) {
        const position = new Vector3(X, Y, Z);
        const coors = [];
        this.coordinates.push(new Vector3(position.x, position.y, position.z));
        for (let i = 0; i < this.coordinates.length; i++) {
            coors.push([this.coordinates[i].x, this.coordinates[i].y, this.coordinates[i].z]);
        }
        this._draw(coors);
        this.profilepoints.push([order, position.x, position.y, position.z]);
        this._checkAndFinish();
    }
    _checkAndFinish() {
        this.toolstate = 0;
        if (this.coordinates.length == this._npoints) {
            const lastPoint = this.secondPoint.getVec3();
            //add the last point only if there was no orthogonal measurement
            //since the last points height is invalid in this case
            if (!this._orthoLine) {
                this.profilepoints.push([this._npoints, lastPoint.x, lastPoint.y, lastPoint.z]);
            }
            super.removeOrthoBase();
            this._orthoLine = null;
            this.linestringfeature.geometry.clear();
            this.linestringfeature.update();
            this.selectedFeature = null;
            this.selectedStart = null;
            //line goes from left to right no matter in what direction it was drawn
            const mh = this.scene.getMeasureHandler();
            const firstPointMap = mh.worldToMouse(this.firstPoint.getVec3());
            const lastPointMap = mh.worldToMouse(lastPoint);
            let sortDirection = false;
            if (Math.abs(firstPointMap.x - lastPointMap.x) > Math.abs(firstPointMap.y - lastPointMap.y)) {
                if (firstPointMap.x < lastPointMap.x)
                    sortDirection = true;
            }
            else {
                if (firstPointMap.y > lastPointMap.y)
                    sortDirection = true;
            }
            this.profilepoints.sort(function (a, b) {
                return sortDirection ? a[0] - b[0] : b[0] - a[0];
            });
            this.coordinates = [];
            for (let i = 0; i < this.profilepoints.length; i++) {
                this.coordinates.push(new Vector3(this.profilepoints[i][1], this.profilepoints[i][2], this.profilepoints[i][3]));
            }
            this.multipointfeature.createSelPoint(this.multipointfeature.geometry);
            this.fire(ProfileTool.finished, this.options["cbobject"], JSON.parse(JSON.stringify(this.coordinates)));
            this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
            if (this.debugexport)
                this._exportAsPointProfile(this.coordinates);
        }
    }
    _draw(coors) {
        //Update the features for drawing
        this.multipointfeature.geometry.coordinates = coors;
        this.multipointfeature.update();
    }
    _cleanUp() {
        if (this.toolstate === 1) {
            this._keyChangeSubscription.unsubscribe();
        }
        if (this.coordinates.length > 0) {
            this.coordinates = [];
            this.multipointfeature.selpointindicator.hide();
        }
        this.firstPoint = null;
        this.secondPoint = null;
        this.multipointfeature.geometry.clear();
        this.linestringfeature.geometry.clear();
        this.pointfeature.geometry.clear();
        this.selectedFeature = null;
        this.selectedStart = null;
        this.featurecollection.update();
        this._npoints = 0;
        this._withOrthoBase = false;
        this._orthoLine = null;
    }
    /**
     *  removes all drawing from the scene resets the tool
     *  @method reset
     *  @public
     */
    reset(evt) {
        super.removeOrthoBase();
        this._cleanUp();
        this.toolstate = 0;
        this.scene.infrascene.render();
        this.fire(ProfileTool.reset, this.options["cbobject"], evt);
    }
    /**
     * Secret hidden feature -> write all actual visible points to console...
     * @method _exportAsPointProfile
     * @private
     */
    _exportAsPointProfile(profile) {
        let s = "";
        for (let i = 0; i < profile.length; i++) {
            s += profile[i].x + "," + profile[i].y + "," + profile[i].z + "\n";
        }
        const blob = new Blob([s]);
        const a = window.document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = "pointprofile.xyz";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }
}
ProfileTool.enabled = "enabled";
ProfileTool.disabled = "disabled";
ProfileTool.continousdraw = "continousdraw";
ProfileTool.pointset = "pointset";
ProfileTool.reset = "reset";
ProfileTool.finished = "finished";
ProfileTool.error = "error";
//# sourceMappingURL=profiletool.js.map